import dayjs from "dayjs";
import React from "react";
import { Trans } from "@lingui/macro";
import { Alert } from "../../../../../UI/Alert/Alert";
import { useDispatch, useSelector } from "react-redux";
import { PopupDetailsSale } from "../popupDetailsSale";
import { Button } from "../../../../../UI/Button/Button";
import { Loader } from "../../../../../UI/Loader/Loader";
import { ButtonLink } from "../../../../../UI/ButtonLink/ButtonLink";
import { numberCurrencyFormat } from "constants/convertToLocalCurrency";
import { OrderedProductsTable } from "./popupTables/orderedProductTable";
import { AcceptedProductsTable } from "./popupTables/acceptedProductTable";
import { RejectedProductsTable } from "./popupTables/rejectedProductTable";
import { getTransPaymentType } from "../../../../../../constants/paymentType";
import { getUserById } from "../../../../../../model/dashboardUsers/dUsersAPI";
import { IDetailSale } from "../../../../../../model/dashboardOrdersArchive/reducer";
import { getSaleByOrderId } from "../../../../../../model/dashboardOrdersArchive/api";
import { PopupPanelDashboard } from "../../../DashboardContentTable/PopupPanelDashboard";
import { selectOrdersArchiveDetailItem } from "../../../../../../model/dashboardOrdersArchive/selectors";
import { dOrdersArchiveClearDetailItemAC } from "../../../../../../model/dashboardOrdersArchive/actionCreators";
import {
  TagIcon,
  UserIcon,
  LinkIcon,
  ClockIcon,
  BookmarkIcon,
  AnnotationIcon,
  ReceiptTaxIcon,
  LocationMarkerIcon,
  OfficeBuildingIcon,
} from "@heroicons/react/outline";
import {
  getColorOrders,
  getTransStatus,
} from "../../../../../../constants/ordersStatus";

export type typePopupDetails = {
  variant?: string;
  setOpenPopup: React.Dispatch<React.SetStateAction<boolean>>;
};

export interface IUser {
  id: string;
  pin: string;
  name: string;
  email: string;
  storeId: string;
  deleted: boolean;
  position: string;
  lang: string | null;
  phoneNumber: string;
}

export const PopupDetails: React.FC<typePopupDetails> = ({
  variant,
  setOpenPopup,
}) => {
  const dispatch = useDispatch();

  const [loading, setLoading] = React.useState<boolean>(true);
  const [openSale, setOpenSale] = React.useState<boolean>(false);
  const [sale, setSale] = React.useState<IDetailSale | undefined>();

  const [users, setUsers] = React.useState<{
    courier?: IUser;
    createdBy?: IUser;
    updatedBy?: IUser;
  }>();

  const dataObject = useSelector(selectOrdersArchiveDetailItem);

  React.useEffect(() => {
    return function () {
      dispatch(dOrdersArchiveClearDetailItemAC());
    };
  }, [dispatch]);

  React.useEffect(() => {
    const getUsers = async () => {
      setLoading(true);

      if (dataObject?.createdBy) {
        const createdByRes = await getUserById(dataObject.createdBy);

        if (createdByRes.status === 200) {
          setUsers((prevState) => {
            return {
              ...prevState,
              createdBy: createdByRes.data,
            };
          });
        }
      }

      if (dataObject?.updatedBy && isNaN(Number(dataObject?.updatedBy))) {
        const updatedByRes = await getUserById(dataObject.updatedBy);

        if (updatedByRes.status === 200) {
          setUsers((prevState) => {
            return {
              ...prevState,
              updatedBy: updatedByRes.data,
            };
          });
        }
      }

      if (dataObject?.courierId) {
        const courierRes = await getUserById(dataObject.courierId);

        if (courierRes.status === 200) {
          setUsers((prevState) => {
            return {
              ...prevState,
              courier: courierRes.data,
            };
          });
        }
      }

      setLoading(false);
    };

    getUsers().catch(console.error);
  }, [dataObject]);

  if (!dataObject) {
    return <Alert type="error" title={<Trans>Have no data.</Trans>} />;
  }

  const onSaleClick = async () => {
    setOpenSale(true);

    getSaleByOrderId(dataObject.id).then((sale) => {
      if (sale.status === 200) {
        setSale(sale.data);
      } else {
        setSale(undefined);
        setOpenSale(true);
      }
    });
  };

  let createdAt = "-";
  if (dataObject?.dateCreated) {
    createdAt = dayjs(dataObject?.dateCreated).format("YYYY-MM-DD, HH:mm");
  }

  let dateUpdated = "-";
  if (dataObject.dateUpdated) {
    dateUpdated = dayjs(dataObject.dateUpdated).format("YYYY-MM-DD, HH:mm");
  }

  const payments = dataObject.payments || [];
  const hasPayments = !!payments.length;

  return (
    <>
      <div className="grid gap-x-8 grid-cols-1 sm:grid-cols-4 border-b sm:border sm:rounded-md sm:px-4">
        <div className="flex  flex-wrap gap-x-4 py-2 border-t sm:py-4 sm:border-t-0 sm:border-b ">
          <div className="flex gap-x-2 items-center text-gray-500 text-left">
            <BookmarkIcon className="h-5 w-5 text-gray-500" />
            <Trans>
              Orders number
            </Trans>:
          </div>

          <div className="flex  self-center text-gray-90 text-left">
            {dataObject.number || "-"}
          </div>
        </div>

        <div className="flex flex-wrap gap-x-4 py-2 sm:col-span-2 sm:py-4 border-t border-b sm:border-t-0">
          <div className="flex gap-x-2 items-center text-gray-500 text-left">
            <OfficeBuildingIcon className="h-5 w-5 text-gray-500" />
            <Trans>Store:</Trans>
          </div>

          <div className="flex  self-center text-gray-90 text-left ">
            {dataObject.store?.name || "-"}
          </div>
        </div>

        <div className="flex flex-wrap gap-x-4 py-2  sm:py-4 border-b ">
          <ClockIcon className="h-5 w-5 text-gray-500" />
          <div className="text-gray-500 text-left">
            <Trans>Creation date</Trans>:
          </div>

          <div className="text-gray-90 text-left">{createdAt}</div>
        </div>
        <div className="flex flex-wrap gap-x-4 py-2 border-t sm:py-4 sm:border-t-0">
          <div className="flex gap-x-2 items-center text-gray-500 text-left">
            <LinkIcon className="h-5 w-5 text-gray-500" />
            <Trans>
              External order number
            </Trans>:
          </div>

          <div
            title={dataObject.externalId || "-"}
            className="text-gray-90 text-left overflow-hidden break-words"
          >
            {dataObject.externalId || "-"}
          </div>
        </div>
        <div className="flex  flex-wrap gap-x-4 py-2 border-t sm:py-4 sm:border-0">
          <ReceiptTaxIcon className="h-5 w-5 text-gray-500" />
          <div className="text-gray-500 text-left">
            <Trans>Payments</Trans>:
          </div>
          <div className="divide-y">
            {hasPayments ? (
              payments.map((item) => {
                let date = "-";
                if (item.date) {
                  date = dayjs(item.date).format("YYYY-MM-DD, HH:mm");
                }

                return (
                  <div key={item.id}>
                    <div className="flex flex-wrap gap-x-2">
                      <span className="text-gray-500">
                        <Trans>data</Trans>
                      </span>

                      <span> :</span>

                      <span className="text-gray-90">{date}</span>
                    </div>

                    <div className="flex flex-wrap gap-x-2 text-left">
                      <span className="text-gray-500">
                        <Trans>amount</Trans>
                      </span>
                      <span>: </span>
                      <span className="text-gray-90">
                        {numberCurrencyFormat(item.amount)}
                      </span>
                      <span className="text-gray-90">
                        (
                        {item.method ? getTransPaymentType(item.method) : " - "}
                        )
                      </span>
                    </div>
                  </div>
                );
              })
            ) : (
              <span className="text-gray-500">
                <Trans>The order not yet paid.</Trans>
              </span>
            )}
          </div>
        </div>

        <div className="flex flex-wrap gap-x-4 py-2 sm:py-4 border-b sm:border-b-0">
          <LinkIcon className="h-5 w-5 text-gray-500" />
          <div className="text-gray-500 text-left">
            <Trans>Sale</Trans>:
          </div>
          <div className="text-gray-90 text-left">
            {dataObject?.saleId ? (
              <ButtonLink
                type="button"
                onClick={onSaleClick}
                label={<Trans>Sale's details</Trans>}
              />
            ) : (
              "-"
            )}
          </div>
        </div>

        <div className="flex flex-wrap gap-x-4 py-2 border-t sm:py-4 sm:border-0">
          <TagIcon className="h-5 w-5 text-gray-500 self-start" />
          <div className="flex  self-start text-gray-500 text-left">
            <Trans>Order's status</Trans>:
          </div>
          <div className="flex  self-start text-gray-90 text-left">
            <span
              className={`bg-${getColorOrders(
                dataObject.status,
              )} rounded w-2 h-2 mr-2 self-center`}
            />

            <span>{getTransStatus(dataObject.status || "-")}</span>
          </div>
        </div>
      </div>

      <div className="mt-4 sm:mt-2.5 grid gap-x-8 grid-cols-1 sm:grid-cols-2 sm:border sm:rounded-md sm:px-4">
        <div className="flex flex-wrap gap-x-4 py-2 sm:py-4 border-t border-b sm:border-t-0">
          <div className="flex gap-x-2 items-center text-gray-500 text-left">
            <UserIcon className="h-5 w-5 text-gray-500" />
            <Trans>
              Client name
            </Trans>:
          </div>

          <div className="flex self-center text-gray-90 text-left font-medium">
            {dataObject.customerName || "-"}
          </div>
        </div>
        <div className="flex flex-wrap gap-x-4 py-2 sm:py-4 border-t border-b sm:border-t-0">
          <div className="flex gap-x-2 items-center text-gray-500 text-left">
            <UserIcon className="h-5 w-5 text-gray-500" />
            <Trans>Client phone</Trans>
            {": "}
          </div>
          <div className="flex  self-center text-gray-90 text-left font-medium">
            {dataObject.phone || "-"}
          </div>
        </div>

        <div className="flex flex-wrap gap-x-4 py-2 border-b sm:col-span-2 sm:py-4">
          <div className="flex gap-x-2 items-center text-gray-500 text-left">
            <LocationMarkerIcon className="h-5 w-5 text-gray-500" />
            <Trans>
              Destination address
            </Trans>:
          </div>
          <div className="text-gray-90 text-left">
            {dataObject.address || "-"}
          </div>
        </div>
        <div className="sm:col-span-2 flex flex-wrap gap-x-4 py-2 border-b sm:border-b-0 sm:py-4">
          <div className="flex gap-x-2 items-center text-gray-500 text-left">
            <AnnotationIcon className="h-5 w-5 text-gray-500" />
            <Trans>Comment</Trans>
            {": "}
          </div>

          <div className="text-gray-90 text-left">
            {dataObject.comment || "-"}
          </div>
        </div>
      </div>

      <div className="mt-4 sm:mt-2.5 grid gap-x-8 grid-cols-1 sm:grid-cols-2 sm:border sm:rounded-md sm:px-4">
        <div className="flex flex-wrap gap-x-4 py-2 sm:py-4 border-t border-b sm:border-t-0 sm:border-b-0">
          <div className="flex gap-x-2 items-center text-gray-500 text-left">
            <Trans>Creator by</Trans>:
          </div>

          <div
            className={`flex text-gray-90 text-left items-center relative ${loading ? "w-3" : ""}`}
          >
            {loading ? (
              <Loader size={3} />
            ) : (
              users?.createdBy?.name || users?.createdBy?.email || "-"
            )}
          </div>
        </div>

        <div className="flex flex-wrap gap-x-4 py-2 border-b sm:border-b-0 sm:py-4">
          <div className="flex gap-x-2 items-center text-gray-500 text-left">
            <Trans>Courier:</Trans>
          </div>

          <div
            className={`flex text-gray-90 text-left items-center relative ${loading ? "w-3" : ""}`}
          >
            {loading ? (
              <Loader size={3} />
            ) : (
              users?.courier?.name || users?.courier?.email || "-"
            )}
          </div>
        </div>
      </div>

      <div className="mt-4 sm:mt-2.5 grid gap-x-8 grid-cols-1 sm:grid-cols-2 sm:border sm:rounded-md sm:px-4">
        <div className="flex flex-wrap gap-x-4 py-2 border-t border-b  sm:py-4 sm:border-t-0 sm:border-b-0">
          <div className="flex gap-x-2 items-center text-gray-500 text-left">
            <Trans>Edited by</Trans>:
          </div>

          <div
            className={`flex text-gray-90 text-left items-center relative ${loading ? "w-3" : ""}`}
          >
            {loading ? (
              <Loader size={3} />
            ) : (
              users?.updatedBy?.name ||
              users?.updatedBy?.email ||
              dataObject.updatedBy ||
              "-"
            )}
          </div>
        </div>

        <div className="flex flex-wrap gap-x-4 py-2 border-b sm:border-b-0 sm:py-4">
          <div className="flex gap-x-2 items-center text-gray-500 text-left">
            <Trans>Date</Trans>:
          </div>

          <div className="flex self-center text-gray-90 text-left">
            {dateUpdated}
          </div>
        </div>
      </div>

      {!!dataObject?.orderedProducts.length && (
        <OrderedProductsTable dataObject={dataObject} variant={variant} />
      )}

      {!!dataObject?.acceptedProducts.length && (
        <AcceptedProductsTable dataObject={dataObject} variant={variant} />
      )}

      {!!dataObject?.rejectedProducts.length && (
        <RejectedProductsTable dataObject={dataObject} variant={variant} />
      )}

      <div className="flex gap-4 mt-5 sm:mt-4 sm:flex sm:col-span-2 justify-end items-end w-full">
        <Button
          type="button"
          secondary={true}
          variant={variant}
          label={<Trans>Close</Trans>}
          onClick={() => setOpenPopup(false)}
        />
      </div>

      <PopupPanelDashboard
        openPopup={openSale}
        setOpenPopup={setOpenSale}
        title={<Trans>Sale's details</Trans>}
      >
        <PopupDetailsSale sale={sale} setOpenPopup={setOpenSale} />
      </PopupPanelDashboard>
    </>
  );
};
